import * as paymentService from '@cart-services/core/paymentService'
import { useFpCampaignPaymentTransactionPolling } from '@cart-services/hooks/use-fp-campaign-payment-transaction-polling'
import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignCartSelector, useFpCampaignAllItems } from '@cart-services/hooks/useFpCampaignCartStore'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { useFpCampaignPaymentService } from '@cart-services/hooks/useFpCampaignPaymentService'
import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { useHandleCheckoutException, type UseHandleCheckoutExceptionArgs } from '@cart-services/hooks/useHandleCheckoutException'
import { getCouponExceptionMessage } from '@cart-services/utils/errorHandlers'
import { DIALOG_COUPON_EXCEPTION, DialogCouponException } from '@cart-ui/DialogCouponException/DialogCouponException'
import { DIALOG_ERROR, DialogError, type DialogErrorProps } from '@cart-ui/DialogError/DialogError'
import { DIALOG_VALIDATE_STOCK, DialogValidateStock, type DialogValidateStockProps } from '@cart-ui/DialogValidateStock/DialogValidateStock'
import { CountryCode, PAYMENT_EXCEPTION_CODES, type ProjectCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { DialogPayMeQRCode } from '@dialog-qr-code/DialogPayMeQRCode'
import { useGa4ClientId } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import type * as TapPay from '@dni-types/payment/tappay'
import { type ResponseErrorGenerics } from '@dni-types/utilities/response'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignActivityInfoImmutable } from '@hooks-api/fortune/useApiCampaignActivityInfoImmutable'
import { useApiGetGuestPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetGuestPaymentTransactionRequest'
import { type CartValidationDetail, type CouponExceptionCodeType } from '@hooks-api/types/dataModel/coupon'
import { ECommerceOrderType, ReceiptType } from '@hooks-api/types/dataModel/eCommercePayments'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { CheckoutPayApplePayModule, CheckoutPayCreditCardModule, isApplePaySupported, isCkoFlowModule, TappayApplePayModule, TappayCreditCardModule } from '@payments'
import { loadTappayScript } from '@payments/lib/utilities'
import { Backdrop } from '@pets-ui/Backdrop/Backdrop'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { Hidden } from '@pets-ui/Hidden/Hidden'
import { HStack } from '@pets-ui/HStack/HStack'
import { NoticeFilled } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndexValues } from '@pets-ui/tokens/zIndex.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { queryStringify } from '@utils'
import { CarrierValidationStatus } from '@wp-checkout/components/InvoiceTypes/carrier-status'
import { type AxiosError } from 'axios'
import { createContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom-v5'
import { type z } from 'zod'
import CheckoutProgress from '../../components/checkout-progress'
import FixedFooter from '../../components/fixed-footer'
import { ErrorInfo, Loading as LoadingIcon } from '../../components/icon'
import Loading from '../../components/loading'
import { OnlineInvoices } from '../../components/online-invoices'
import { TheCampaignHeader } from '../../components/TheCampaignHeader'
import { useCampaign } from '../../hooks/use-campaign'
import { useMember } from '../../hooks/use-member'
import { CampaignStatus } from '../../model/campaign'
import style from '../../style/pages/client-payments.module.scss'
import { CampaignApplePayDialog } from './checkout/CampaignApplePayDialog'
import { CampaignCreditCardDialog } from './checkout/CampaignCreditCardDialog'
import { CheckoutProvider } from './checkout/components/CheckoutProvider/CheckoutProvider'
import { PaymentListItem } from './checkout/components/PaymentListItem/PaymentListItem'
import { useFpCampaignCkoFlowPayment } from './checkout/useFpCampaignCkoFlowPayment'
import { invoiceSchema } from './zods/invoce-schema'

const APP_ENV = import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox'
const validateStatus = (status?: CampaignStatus) => status === CampaignStatus.IN_PROGRESS || status === CampaignStatus.TESTING

export const PaymentContext = createContext(

  {} as {
    setIsCheckoutDisabledConditions: (condition: Record<string, boolean>) => void
    setIsAppleSetup: (condition: boolean) => void
  },
)

export const ClientPayments = () => {
  const history = useHistory()
  const { campaignCodeFromUrl } = useCampaign()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const campaignId = campaign?.campaign_id ?? undefined
  const { region: countryCode } = campaign || {}
  const { projectCode } = useFpCampaignProductsService(campaignCodeFromUrl)
  const { t } = useTranslation()
  const { search } = useLocation()
  // NOTE: legacy code 有包 context 所需要的 props，所以不能刪
  const [, setIsCheckoutDisabled] = useState({} satisfies Record<string, boolean>)
  const [isCreditCardValid, setIsCreditCardValid] = useState(false)
  const orderHashFromUrl = new URLSearchParams(search).get('orderHash')
  const redirectFromThankyou = new URLSearchParams(search).get('redirectFromThankyou') === 'true'
  const { hasToken } = useMember()

  const { destructuredData: exhibitorSettings, isLoading: isActivityLoading } = useApiCampaignActivityInfoImmutable(campaignCodeFromUrl, {
    onSuccess: ({ data }) => {
      const { status } = data.data
      if (!validateStatus(status)) {
        history.push(`/campaigns/${campaignCodeFromUrl}`)
      }
    },
  })
  const invoice = useFpCampaignCartSelector(exhibitorSettings?.campaign_id ?? null, state => state.invoice)
  const campaignCarrierStatus = useFpCampaignCartSelector(exhibitorSettings?.campaign_id ?? null, state => state.invoice.carrierValidationStatus)
  const regularGiveaways = useFpCampaignCartSelector(exhibitorSettings?.campaign_id ?? null, state => state.coupon.selectedRegularGiveaways)
  const billingAddressEmail = useFpCampaignCartSelector(exhibitorSettings?.campaign_id ?? null, state => state.billing.billingAddressEmail)
  const appliedPaymentMethodId = useFpCampaignCartSelector(exhibitorSettings?.campaign_id ?? null, state => state.payment.appliedPaymentMethodId)
  const cartItems = useFpCampaignAllItems(exhibitorSettings?.campaign_id ?? null)
  const { destructuredData: pendingOrder, isLoading: isLoadingPendingOrder } = useApiGetGuestPaymentTransactionRequest({ orderHash: orderHashFromUrl || '' })
  const { isCartEmpty, resetCartValues } = useFpCampaignCartService(exhibitorSettings?.campaign_id ?? null)
  const country = exhibitorSettings?.available_regions[0]
  const { openDialog: openDialogCouponException } = useDialog(DIALOG_COUPON_EXCEPTION)
  const { openDialog: openDialogValidateStock } = useDialog<DialogValidateStockProps>(DIALOG_VALIDATE_STOCK)
  const { openDialog: openDialogError, closeDialog: closeDialogError } = useDialog<DialogErrorProps>(DIALOG_ERROR)

  const { removeSelectedRegularGiveaways, resetCouponsAndGiveaways } = useFpCampaignCouponService(
    exhibitorSettings?.campaign_id ?? null,
    country ?? CountryCode.TW,
    campaignCodeFromUrl,
  )

  const {
    processPayment,
    processPendingOrderPayment,
    executePaymentProcess,
    payMePaymentQRCodeUrl,
    paymentTransactionId,
    updatePendingOrderInvoice,
    paymentMethodsGroupByType,
    isProcessing,
    appliedPaymentInfo,
    paymentModule,
    configPaymentModule,
    setDefaultPaymentMethod,
    initPaymentsSdk,
    errors: campaignPaymentServiceErrors,
    isCalculateLoading,
    hasCreatePaymentTransaction,
    isValidatingInitPayment,
    isSuccessRedirect,
    setIsPaymentProcessing,
    isPaymentProcessing,
    userPaymentTokenInfo,
    createCampaignCartTransaction,
    rememberPaymentCardToken,
  } = useFpCampaignPaymentService({
    campaignId: exhibitorSettings?.campaign_id ?? null,
    campaignCode: campaignCodeFromUrl,
    orderType: orderHashFromUrl ? ECommerceOrderType.TRANSACTION : ECommerceOrderType.CART,
    orderId: pendingOrder?.id,
    orderHash: orderHashFromUrl,
    paymentSuccessUrl: window.location.href,
    paymentFallbackUrl: window.location.href,
    countryCode,
    onCreatePaymentTransactionError: (error) => {
      const createPaymentTransactionError = error.response?.data.error
      if (!createPaymentTransactionError) return
      if (createPaymentTransactionError.code === 9003) {
        datadogRum.addError(new Error(`Stock Exception code=${createPaymentTransactionError.code}`), {
          function: '[onCreatePaymentTransactionError] 庫存不足',
        })
        const unavailableProductId = createPaymentTransactionError.detail?.product_id
        const unavailableProductName = createPaymentTransactionError.detail?.product_name
        const unavailableLeftQuantity = createPaymentTransactionError.detail?.left_quantity
        const unavailableCartItem = cartItems.find(item => Number(item.cartItemId) === unavailableProductId)
        const unavailableRegularGiveawayProduct = regularGiveaways.find(giveaway => giveaway.products.find(product => Number(product.product_id) === unavailableProductId))
        if (unavailableCartItem) {
          openDialogValidateStock({
            productName: unavailableProductName,
            leftQuantity: unavailableLeftQuantity,
            onConfirm: () => {
              window.location.href = `${window.location.origin}/campaigns/${campaignCodeFromUrl}/cart`
            },
          })
        }
        if (unavailableRegularGiveawayProduct) {
          openDialogValidateStock({
            productName: unavailableProductName,
            leftQuantity: unavailableLeftQuantity,
            onConfirm: () => {
              removeSelectedRegularGiveaways(unavailableRegularGiveawayProduct.coupon_criterion_id)
              window.location.href = `${window.location.origin}/campaigns/${campaignCodeFromUrl}/cart`
            },
          })
        }
      }
    },
  })

  const handleCouponException = useCallbackEvent((code?: CouponExceptionCodeType, detail?: CartValidationDetail) => {
    const errorMessage = getCouponExceptionMessage(code, detail)
    openDialogCouponException({
      message: t(errorMessage.message, { ...errorMessage?.value, defaultValue: errorMessage.message }),
      onConfirm: () => {
        resetCouponsAndGiveaways()
        history.push(`/merchant/${campaignCodeFromUrl}/cart`)
      },
    })
    datadogRum.addError(new Error(`Coupon Exception code=${code}`), {
      function: `[handleCouponException] ${detail?.message}`,
      detail,
    })
  })

  const handleCustomerApiException: UseHandleCheckoutExceptionArgs['handleCustomerApiException'] = useCallbackEvent((error) => {
    const message = error.response?.data.error.message || '結帳失敗'
    datadogRum.addError(error, {
      function: '[processPayment] 結帳失敗',
      message,
    })
    alert(message)
  })

  const handleTheOthersApiException: UseHandleCheckoutExceptionArgs['handleTheOthersApiException'] = useCallbackEvent((error) => {
    const errorData = (error as AxiosError<{ error: ResponseErrorGenerics }>).response?.data.error
    let message = `${t('wpCheckout.checkoutFailed', { defaultValue: '結帳失敗' })}`
    /**
     * 如果是後端吐 13001 的錯誤，則顯示後端吐的錯誤訊息，否則顯示預設錯誤訊息
     */
    if (errorData?.code === PAYMENT_EXCEPTION_CODES.PAYMENT_API_FAILED) {
      message = errorData.message
    }
    datadogRum.addError(error, {
      function: '[processPayment] 結帳失敗',
      message,
    })
    alert(message)
  })

  const handleUnexpectedException = useCallbackEvent((error: unknown) => {
    datadogRum.addError(error, {
      function: '[processPayment] 非預期的 JS 錯誤：結帳失敗',
      error,
    })
    // 非 AxiosError（含流程自丟的 Error）落這裡；只記 datadog 使用者會看到轉圈不動而重複送出
    openDialogError({
      title: '訂單未完成結帳',
      description: '付款發生問題，請稍後再試或選擇其他付款方式。',
      confirmText: '關閉',
      onConfirm: () => closeDialogError(),
    })
  })

  const { handleCheckoutException } = useHandleCheckoutException({
    handleCouponException,
    handleCustomerApiException,
    handleTheOthersApiException,
    handleUnexpectedException,
  })

  const isOfflinePayment = appliedPaymentInfo?.payment_method.type === PaymentMethodType.OFFLINE_PAYMENT

  const paymentMethods = useMemo(() => {
    const pendingOrderPaymentMethods = paymentService.getPaymentMethodsGroupByType(pendingOrder?.payment_methods ?? [])
    const _paymentMethods = orderHashFromUrl && pendingOrder ? pendingOrderPaymentMethods : paymentMethodsGroupByType
    const ckoApplePayOnly = _paymentMethods?.flatMap(([type, methods]): [PaymentMethodType, typeof methods][] => {
      if (type !== PaymentMethodType.APPLE_PAY) return [[type, methods]]
      const ckoMethods = methods.filter(method => method.service_type === PaymentServiceType.CHECKOUTPAY)
      return ckoMethods.length > 0 ? [[type, ckoMethods]] : []
    })
    /** Safari: 所有 Apple Pay；Chrome: 僅 CKO */
    return isApplePaySupported() ? _paymentMethods : ckoApplePayOnly
  }, [orderHashFromUrl, paymentMethodsGroupByType, pendingOrder])

  const queryParams = new URLSearchParams(history.location.search)
  const isHideBackBtn = queryParams.has('is_need_change') && queryParams.get('is_need_change') === '1'
  const isPayFailed = queryParams.has('is_pay_failed') && queryParams.get('is_pay_failed') === '1'
  const ga4ClientIdStore = useGa4ClientId()

  // NOTE: legacy code 有包 context 所需要的 props，所以不能刪
  const [, setIsAppleSetup] = useState(false)
  const setIsCheckoutDisabledConditions = useCallbackEvent((condition: Record<string, boolean>) => {
    setIsCheckoutDisabled(prev => ({ ...prev, ...condition }))
  })

  const contextValue = useMemo(() => ({
    setIsAppleSetup,
    setIsCheckoutDisabledConditions,
  }), [setIsCheckoutDisabledConditions])

  const buildPaymentSuccessPath = (orderHash: string) => `/campaigns/${campaignCodeFromUrl}/thankyou/${orderHash}`
  const buildPaymentFallbackPath = (orderHash: string) => `/campaigns/${campaignCodeFromUrl}/payments?${queryStringify({ orderHash, paymentError: true })}`
  const buildSuccessUrl = (orderHash: string) => `${window.location.origin}${buildPaymentSuccessPath(orderHash)}`
  const buildFallbackUrl = (orderHash: string) => `${window.location.origin}${buildPaymentFallbackPath(orderHash)}`

  const {
    execute: executeCkoFlowPayment,
    applePayDialogContext,
    closeApplePayDialog,
    creditCardDialogContext,
    closeCreditCardDialog,
  } = useFpCampaignCkoFlowPayment({
    paymentModule: isCkoFlowModule(paymentModule) ? paymentModule : null,
    rememberPaymentCardToken,
    billingAddressEmail,
    buildSuccessUrl,
    buildFallbackUrl,
    createCampaignCartTransaction,
  })

  useEffect(function redirectToCampaignHomeWhenOrderNotPending() {
    if (!pendingOrder) return
    // PayMe QR 顯示期間 completed／failed 由輪詢導向 thankyou／失敗頁，不搶導；cancelled／refunded 輪詢不處理，照舊導回
    const isPayMePollingOwnedStatus = pendingOrder.status === FastpassOrderStatus.COMPLETED || pendingOrder.status === FastpassOrderStatus.FAILED
    if (payMePaymentQRCodeUrl && isPayMePollingOwnedStatus) return
    if (pendingOrder.status !== FastpassOrderStatus.PENDING) {
      resetCartValues()
      history.push(`/campaigns/${campaignCodeFromUrl}`)
    }
  }, [campaignCodeFromUrl, history, payMePaymentQRCodeUrl, pendingOrder, resetCartValues])

  const [invoiceError, setInvoiceError] = useState<z.ZodError | null>(null)
  const [isPendingCarrierSubmit, setIsPendingCarrierSubmit] = useState(false)
  /** Apple Pay / 信用卡 dialog 確定要整頁導頁那一刻切 true，蓋原本只有按鈕 spinner 的空窗。 */
  const [isDialogRedirecting, setIsDialogRedirecting] = useState(false)

  const handleProceedPayment = useCallbackEvent(async () => {
    try {
      setIsPaymentProcessing(true)
      const isApplePay = appliedPaymentInfo?.payment_method.type === PaymentMethodType.APPLE_PAY
      if (isApplePay && paymentModule instanceof TappayApplePayModule) {
        await paymentModule.createApplePayToken()
      }
      if (pendingOrder?.id && pendingOrder.order_hash) {
        // 代付款 CKO 一律走 dialog（inline 容器與 executePaymentProcess route1 已移除），先寫回發票再開；
        // token（TapPay / ECPay）維持 processPendingOrderPayment
        if (paymentModule && isCkoFlowModule(paymentModule)) {
          await updatePendingOrderInvoice(pendingOrder.id)
          if (!appliedPaymentMethodId) {
            throw new Error('[handleProceedPayment] appliedPaymentMethodId is required for CKO flow payment')
          }
          await executeCkoFlowPayment({
            paymentOptionId: appliedPaymentMethodId,
            headers: ga4ClientIdStore.current
              ? { 'X-Ga-Client-Id': ga4ClientIdStore.current }
              : undefined,
            existingTransaction: { id: pendingOrder.id, order_hash: pendingOrder.order_hash },
          })
        }
        else {
          await processPendingOrderPayment({
            orderId: pendingOrder.id,
            orderHash: pendingOrder.order_hash,
            payment_success_url: buildPaymentSuccessPath,
            payment_fallback_url: buildPaymentFallbackPath,
          })
        }
      }
      else if (paymentModule && isCkoFlowModule(paymentModule)) {
        if (!appliedPaymentMethodId) {
          throw new Error('[handleProceedPayment] appliedPaymentMethodId is required for CKO flow payment')
        }
        await executeCkoFlowPayment({
          paymentOptionId: appliedPaymentMethodId,
          headers: ga4ClientIdStore.current
            ? { 'X-Ga-Client-Id': ga4ClientIdStore.current }
            : undefined,
        })
      }
      else {
        await processPayment({
          payment_success_url: buildPaymentSuccessPath,
          payment_fallback_url: buildPaymentFallbackPath,
          headers: ga4ClientIdStore.current
            ? {
              'X-Ga-Client-Id': ga4ClientIdStore.current,
            }
            : undefined,
        })
      }
    }
    catch (error) {
      handleCheckoutException(error)
    }
    finally {
      setIsPaymentProcessing(false)
    }
  })

  useEffect(function resumePendingCarrierSubmitWhenValidationSettles() {
    if (!isPendingCarrierSubmit) return
    if (campaignCarrierStatus === CarrierValidationStatus.VALIDATING) return
    setIsPendingCarrierSubmit(false)
    if (campaignCarrierStatus !== CarrierValidationStatus.NOT_FOUND) {
      handleProceedPayment()
    }
  }, [campaignCarrierStatus, isPendingCarrierSubmit, handleProceedPayment])

  const handleCheckout = async () => {
    const parsedInvoice = invoiceSchema.safeParse({ ...invoice, isOfflinePayment })
    if (!parsedInvoice.success) {
      setInvoiceError(parsedInvoice.error)
      return
    }

    if (invoice.receiptType === ReceiptType.PHONE_CARRIER && !isOfflinePayment) {
      if (campaignCarrierStatus === CarrierValidationStatus.NOT_FOUND) return
      if (campaignCarrierStatus === CarrierValidationStatus.VALIDATING) {
        setIsPendingCarrierSubmit(true)
        return
      }
    }

    await handleProceedPayment()
  }

  const [isPaymentFallbackError, setIsPaymentFallbackError] = useState(false)
  useEffect(() => {
    const handlePaymentFallbackError = () => {
      const paymentFallbackError = new URLSearchParams(search).get('paymentError')
      if (paymentFallbackError) {
        setIsPaymentFallbackError(true)
        const orderHashFromUrl = new URLSearchParams(search).get('orderHash')
        window.history.replaceState(null, '', `${window.location.pathname}?${queryStringify({ orderHash: orderHashFromUrl })}`)
      }
    }
    handlePaymentFallbackError()
  }, [search])

  useEffect(() => {
    loadTappayScript().catch((error) => {
      console.error('[client-payments] TapPay SDK preload failed', error)
      datadogRum.addError(error, { function: '[client-payments] TapPay SDK preload' })
    })
  }, [])

  /**
   * 設定預設付款方式
   */
  useEffect(() => {
    setDefaultPaymentMethod()
  }, [setDefaultPaymentMethod])

  /**
   * 初始化付款 SDK
   */
  useEffect(() => {
    initPaymentsSdk()
  }, [initPaymentsSdk])

  /**
   * 設定第三方 SDK 所需資訊，付款方式改變時會觸發
   */
  useEffect(() => {
    if (orderHashFromUrl || !exhibitorSettings?.currency) return
    configPaymentModule(APP_ENV, exhibitorSettings.currency)
  }, [configPaymentModule, exhibitorSettings?.currency, orderHashFromUrl])

  /**
   * 當付款失敗時會 history.push orderHash，需要重新 configPaymentModule
   */
  useEffect(() => {
    if (!orderHashFromUrl || !pendingOrder) return
    configPaymentModule(APP_ENV, pendingOrder.currency)
  }, [configPaymentModule, orderHashFromUrl, pendingOrder])

  // CheckoutPay 信用卡驗證（Flow SDK 路徑 — 走 drop-in 的 onChange → component.isValid()）
  useEffect(function bindCkoFlowValidationChange() {
    if (!(paymentModule instanceof CheckoutPayCreditCardModule)) return
    return paymentModule.onValidationChange((isValid) => {
      setIsCreditCardValid(isValid)
    })
  }, [paymentModule])

  // Tappay 信用卡驗證
  useEffect(() => {
    if (!(paymentModule instanceof TappayCreditCardModule)) {
      return
    }
    const eventHandler = (data: TapPay.CardUpdate) => {
      if (paymentModule instanceof TappayCreditCardModule) {
        console.log('[client-payments] cardUpdate', data)
        setIsCreditCardValid(data.canGetPrime)
      }
    }
    paymentModule.addEventListener('cardUpdate', eventHandler)
    return () => {
      paymentModule.removeEventListener('cardUpdate', eventHandler)
    }
  }, [paymentModule])

  useEffect(() => {
    if (!country || (!orderHashFromUrl && isCartEmpty && !hasCreatePaymentTransaction)) {
      history.push(`/campaigns/${campaignCodeFromUrl}/checkout`)
    }
  }, [campaignCodeFromUrl, country, history, isCartEmpty, hasCreatePaymentTransaction, orderHashFromUrl])

  const payMeOrderHash = orderHashFromUrl || (paymentTransactionId ? String(paymentTransactionId) : '')
  const { startPolling, stopPolling } = useFpCampaignPaymentTransactionPolling({
    orderHash: payMeOrderHash,
    basePath: `/campaigns/${campaignCodeFromUrl}`,
  })
  useEffect(function pollPayMeTransactionWhileQRCodeShown() {
    if (!payMePaymentQRCodeUrl) return
    startPolling()
    return () => stopPolling()
  }, [payMePaymentQRCodeUrl, startPolling, stopPolling])

  const handleRefreshPayMePayment = useCallbackEvent(() => executePaymentProcess({
    orderHash: payMeOrderHash,
    orderId: pendingOrder?.id,
    payment_success_url: buildPaymentSuccessPath,
    payment_fallback_url: buildPaymentFallbackPath,
  }))

  // CKO Flow 信用卡改在 dialog 內填卡，送出鈕不再以 isCreditCardValid 擋；TapPay 仍走原 token gate。
  const isDisableCheckoutOnCreditCard = !isCreditCardValid && !userPaymentTokenInfo && paymentModule instanceof TappayCreditCardModule
  const isLoading = isActivityLoading || isCalculateLoading || isLoadingPendingOrder || isSuccessRedirect
  const isPaymentFail = isPayFailed || !!campaignPaymentServiceErrors.executePayment || !!campaignPaymentServiceErrors.getPrimeError || isPaymentFallbackError

  const createPaymentTransactionError = useMemo(() => {
    if (campaignPaymentServiceErrors.createPaymentTransaction?.response?.data.error.code === 9003) return '庫存不足，建立訂單失敗，請回購物車調整庫存數量'
    return '建立訂單失敗'
  }, [campaignPaymentServiceErrors.createPaymentTransaction])

  return (
    exhibitorSettings
    && (
      <CheckoutProvider
        orderType={ECommerceOrderType.CART}
        campaignId={exhibitorSettings.campaign_id}
        campaignCode={campaignCodeFromUrl}
        currency={exhibitorSettings.currency}
      >
        <PaymentContext.Provider value={contextValue}>
          {isProcessing && !isLoading && (
            <div className={style['validating-wrapper']}>
              <div className={style['icon-wrapper']}>
                <LoadingIcon />
                {' '}
                <span>{t('payment.validating')}</span>
              </div>
            </div>
          )}
          <main className={style.main}>
            <TheCampaignHeader projectCode={projectCode as ProjectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
            <CheckoutProgress currentStep={2} />
            <Hidden hidden={!isLoading}><Loading /></Hidden>
            <Hidden hidden={isLoading}>
              <div className={style.errors}>
                {isPaymentFail && (
                  <div className={style.error}>
                    <ErrorInfo />
                    <span>{t('order.transactionFailed')}</span>
                  </div>
                )}
                {redirectFromThankyou && (
                  <HStack alignItems="center">
                    <NoticeFilled color={colors.red600} fontSize={16} />
                    <Text color={colors.red600}>訂單未完成付款</Text>
                  </HStack>
                )}
              </div>
              {!!campaignPaymentServiceErrors.createPaymentTransaction && (
                <div className={style.errors}>
                  <div className={style.error}>
                    <ErrorInfo />
                    <span>{createPaymentTransactionError}</span>
                  </div>
                </div>
              )}
              <VStack gap={2} p={2.5}>
                <Text variant="h5">付款方式</Text>
                <VStack gap={3}>
                  {paymentMethods?.map(([type, paymentMethods]) => (
                    <PaymentListItem
                      key={type}
                      paymentMethods={paymentMethods}
                      isUserLoggedIn={hasToken}
                      isDisabled={isPaymentProcessing}
                    />
                  ))}
                </VStack>
              </VStack>
              {appliedPaymentInfo
              && (
                <OnlineInvoices
                  isInvoiceEnable={country === 'TW' && !isOfflinePayment}
                  pickedPayment={appliedPaymentInfo?.payment_method.type}
                  campaignId={exhibitorSettings.campaign_id}
                  invoiceError={invoiceError}
                  countryCode={countryCode ?? CountryCode.TW}
                />
              )}
              <FixedFooter classes={style.footer}>

                {!isHideBackBtn && (
                  <Button
                    fullWidth
                    disabled={!!orderHashFromUrl}
                    variant="outlined"
                    onClick={() => {
                      window.location.href = `${window.location.origin}/campaigns/${campaignCodeFromUrl}/checkout`
                    }}
                  >
                    {t('button.return')}
                  </Button>
                )}
                <Button
                  fullWidth
                  onClick={handleCheckout}
                  loading={isPaymentProcessing || isPendingCarrierSubmit}
                  disabled={isDisableCheckoutOnCreditCard || isValidatingInitPayment || isPaymentProcessing || isPendingCarrierSubmit}
                >
                  {t('button.next')}
                </Button>
              </FixedFooter>
            </Hidden>
          </main>
        </PaymentContext.Provider>
        {applePayDialogContext && paymentModule instanceof CheckoutPayApplePayModule && countryCode && (
          <CampaignApplePayDialog
            context={applePayDialogContext}
            paymentModule={paymentModule}
            countryCode={countryCode}
            onClose={closeApplePayDialog}
            onError={(error) => {
              // 不關窗、不走 handleCheckoutException（會疊一層全域錯誤 modal）：
              // dialog 內建 hasError 狀態已顯示「更換付款方式/重新再試」，這裡只記錯誤。
              datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
                function: '[ClientPayments] CampaignApplePayDialog onError',
              })
            }}
            onRedirecting={() => setIsDialogRedirecting(true)}
          />
        )}
        {creditCardDialogContext && paymentModule instanceof CheckoutPayCreditCardModule && (
          <CampaignCreditCardDialog
            context={creditCardDialogContext}
            paymentModule={paymentModule}
            countryCode={countryCode ?? CountryCode.TW}
            isCreditCardValid={isCreditCardValid}
            onClose={() => {
              closeCreditCardDialog()
              history.replace({ search: `?orderHash=${creditCardDialogContext.orderHash}` })
            }}
            onError={(error) => {
              closeCreditCardDialog()
              // 交易單已在開 dialog 前建好；不論哪種失敗都導回待付款頁重試，避免再次送出建出第二筆孤兒訂單。
              history.replace({ search: `?orderHash=${creditCardDialogContext.orderHash}` })
              handleCheckoutException(error)
            }}
            onRedirecting={() => setIsDialogRedirecting(true)}
          />
        )}
        {!!payMePaymentQRCodeUrl && (
          <DialogPayMeQRCode
            url={payMePaymentQRCodeUrl}
            onRefresh={payMeOrderHash ? handleRefreshPayMePayment : undefined}
          />
        )}
        <DialogValidateStock />
        <DialogCouponException />
        <DialogError />
        <Backdrop open={isDialogRedirecting} zIndex={zIndexValues.snackbar}>
          <CircularProgress fontSize={36} color={colors.white} />
        </Backdrop>
      </CheckoutProvider>
    )
  )
}
